import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TerminalIcon from "@mui/icons-material/Terminal";
import { useFetchContext } from "../../../utils/fetch";
import { useAuthHeaders } from "../../../utils/query/query";
import {
  ConnectBlock,
  LoginSteps,
  fetchLoginSteps,
  isErrorBlock,
  parseInstructions,
} from "./connectApi";

/**
 * How to reach this cluster, matching V2's CONNECT dialog: one bordered card headed "Connection
 * Instructions", numbered steps as plain text, and dark code blocks with a Copy control in the
 * top right of each.
 *
 * The steps come from the server rather than being composed here, because they are per-cloud and
 * per-cluster: which account to assume into, which debugger pod, which kubeconfig command.
 *
 * **The response contains live temporary credentials.** For AWS the endpoint mints STS keys
 * through AssumeRole at request time and embeds them in the export block, so the query is scoped
 * to the dialog being open and cached for zero milliseconds, and nothing here writes the payload
 * to the console or into an error message.
 */
export default function ConnectDialog({
  open,
  onClose,
  clusterName,
  organizationId,
}: {
  open: boolean;
  onClose: () => void;
  clusterName?: string;
  organizationId?: string;
}) {
  const context = useFetchContext();
  const authHeaders = useAuthHeaders();
  const [copied, setCopied] = useState<number | null>(null);

  const query = useQuery<LoginSteps>(
    ["cluster-login-steps", clusterName, organizationId],
    () =>
      fetchLoginSteps(
        context,
        authHeaders,
        clusterName as string,
        organizationId
      ),
    {
      // Only while the dialog is open, and never cached: these are credentials, and holding them
      // in the query cache would keep them alive long after the dialog is dismissed.
      enabled: open && Boolean(clusterName),
      cacheTime: 0,
      staleTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  const blocks = useMemo(
    () => parseInstructions(query.data?.instructions),
    [query.data]
  );
  const degraded = blocks.some(isErrorBlock);

  const copy = (text: string, index: number) => {
    navigator.clipboard.writeText(text).then(
      () => setCopied(index),
      () => setCopied(null)
    );
  };

  const renderBlock = (block: ConnectBlock, index: number) => {
    if (block.kind === "text") {
      return (
        <Typography
          key={index}
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}
        >
          {block.text}
        </Typography>
      );
    }
    const failed = isErrorBlock(block);
    return (
      <Box
        key={index}
        sx={{
          position: "relative",
          borderRadius: 1,
          bgcolor: failed ? "#3a2c1e" : "#1f1f1f",
          color: "#f5f5f5",
          px: 2,
          py: 1.75,
        }}
      >
        {!failed && (
          <Button
            size="small"
            startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
            onClick={() => copy(block.code, index)}
            sx={{
              position: "absolute",
              top: 6,
              right: 8,
              color: "#e0e0e0",
              textTransform: "none",
              minWidth: 0,
              "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
            }}
          >
            {copied === index ? "Copied" : "Copy"}
          </Button>
        )}
        <Box
          component="pre"
          sx={{
            m: 0,
            mt: failed ? 0 : 2.5,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 13,
            lineHeight: 1.65,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          {block.code}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1.5,
        }}
      >
        <span>Connect to {clusterName}</span>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {query.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not load the connect instructions for this cluster.
          </Alert>
        )}

        {query.isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : !query.data ? null : (
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <TerminalIcon fontSize="small" />
              <Typography variant="subtitle1" fontWeight={600}>
                Connection Instructions
              </Typography>
            </Stack>

            {/* Not in V2, kept because the response carries live credentials and the window is
                easy to screen-share without thinking. One line rather than a banner. */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 2 }}
            >
              Contains temporary credentials for this cluster's account. Avoid
              pasting this window into a ticket, a chat or a shared screen.
            </Typography>

            {degraded && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Part of these instructions could not be generated, so a step
                below carries an error comment instead of a command. The rest is
                still usable.
              </Alert>
            )}

            <Stack spacing={1.75}>{blocks.map(renderBlock)}</Stack>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{ borderRadius: 5, px: 3 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
